import { UploadImgModel } from '@mm/state/model/UploadImgModel';
import { openImageUploadFormModal } from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { Component, MouseEvent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import 'spinkit/css/spinkit.css';
import { CropArea } from '../CropArea/CropArea';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import './ImageUpload.scss';

export const ImageUploadConstants = {
  OVERSIZE_MESSAGE: 'The image should be less than 10mb',
  MAX_IMAGE_SIZE: 10485760,
};

const getFormatFromType = type => type.split('/').pop();

type OuterProps = {
  className?: string;
  imgUrl: string;
  hideCropIcon?: boolean;
  couldBeRemoved?: boolean;
  iconSize?: number;
  fixedRatio?: boolean;
  uploadImage: (image?: UploadImgModel) => Promise<any>;
  showLoader?: boolean;
  title?: string;
  removeImage: (image?: any) => any;
  shouldCroppedImmediately?: boolean;
  couldShowUploadFormModal?: boolean;
  onRatioChange: (isImageSquare?: boolean) => any;
  cropOnToggle?: boolean;
  onCancelButtonClick?: () => any;
  onCancelCrop?: () => any;
  iconArea?: (
    onUpload: (e?: MouseEvent<HTMLDivElement>) => any,
  ) => ReactNode | Element[];
};

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

type State = {
  shouldShowCropper: boolean;
  fixedRatio: boolean;
  shouldShowImageSizeModal: boolean;
  uploading: boolean;
  justLoaded: boolean;
  shouldCropOnToggle?: boolean;
};

class ImageUpload extends Component<Props, State> {
  inputFile?: HTMLInputElement;
  cropper?: any;

  static classnameBySize = new Map()
    .set(1, 'small')
    .set(2, 'medium')
    .set(3, 'large');

  static defaultProps = {
    couldBeRemoved: false,
    iconSize: 3,
    removeImage: () => {},
    onCancelButtonClick: () => {},
    onRatioChange: () => {},
    onCancelCrop: () => {},
    imgUrl: null,
    title: '',
    shouldCroppedImmediately: false,
    couldShowUploadFormModal: false,
    showLoader: false,
    hideCropIcon: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      shouldShowCropper: false,
      fixedRatio: this.props.fixedRatio || false,
      shouldShowImageSizeModal: false,
      uploading: false,
      justLoaded: false,
    };
  }

  // tslint:disable-next-line: function-name
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      (this.state.uploading && nextProps.imgUrl !== this.props.imgUrl) ||
      nextProps.showLoader === false
    ) {
      this.setState({ uploading: false });
    }
    this.setState({
      shouldCropOnToggle: nextProps.cropOnToggle,
      fixedRatio: nextProps.fixedRatio || false,
    });
  }

  onKeyDown = event => {
    const { key } = event;
    switch (key) {
      case ENTER_KEY_NAME: {
        this.onCancelCropper();

        break;
      }

      default:
    }
  };

  onUpload = (e?: MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (this.inputFile) {
      this.inputFile.click();
    }
  };

  onFormModalCancelClick = () => {
    this.props.removeImage();
  };

  onFormModalNextClick = (isImageSquare: boolean) => {
    this.props.onRatioChange(isImageSquare);
    this.setState({
      fixedRatio: isImageSquare,
      shouldShowCropper: true,
    });
  };

  onFileUpload = async e => {
    e.stopPropagation();
    e.preventDefault();

    const { shouldCroppedImmediately, couldShowUploadFormModal } = this.props;
    const image = e.target.files[0];
    if (!image) {
      return;
    }

    e.target.value = ''; // reset value to be able to upload this file again
    if (image.size > ImageUploadConstants.MAX_IMAGE_SIZE) {
      this.setState({
        shouldShowImageSizeModal: true,
      });
      return;
    }
    this.setState({
      uploading: true,
      justLoaded: true,
    });
    await this.props.uploadImage({
      content: image,
      extension: getFormatFromType(image.type),
    });

    if (shouldCroppedImmediately) {
      this.setState({
        shouldShowCropper: !couldShowUploadFormModal,
      });

      if (couldShowUploadFormModal) {
        this.props.openImageUploadFormModal(
          this.onFormModalNextClick,
          this.onFormModalCancelClick,
        );
      }
    }
  };

  onImageSizeModalHide = () => {
    this.setState({
      shouldShowImageSizeModal: false,
    });
    this.onUpload();
  };

  onCancelCropper = () => {
    const { shouldCroppedImmediately, onCancelCrop } = this.props;
    if (shouldCroppedImmediately && this.state.justLoaded && onCancelCrop) {
      onCancelCrop();
    }
    this.hideCropper();
    this.setState({ shouldCropOnToggle: false });
  };

  setCropperVisibility = value => () => {
    this.setState({
      shouldShowCropper: value,
    });
    if (value) {
      window.addEventListener('keydown', this.onKeyDown);
    } else {
      window.removeEventListener('keydown', this.onKeyDown);
    }
  };

  showCropper = this.setCropperVisibility(true);
  hideCropper = this.setCropperVisibility(false);

  ref = node => {
    if (!node) return;

    this.inputFile = node;
  };

  crop = (blob: Blob) => {
    const { imgUrl, uploadImage } = this.props;
    const extension = imgUrl.split('.').pop() || '';

    uploadImage({
      content: blob,
      extension,
    });

    this.setState({
      uploading: true,
      shouldShowCropper: false,
      justLoaded: false,
    });
  };

  renderBackgroundImage() {
    const { imgUrl } = this.props;
    return (
      <img
        src={imgUrl}
        className={classnames('image-upload__background-image', {
          'image-upload__background-image--uploading': this.state.uploading,
        })}
        alt="Sorry"
      />
    );
  }

  renderCropIcon() {
    const { showCropper } = this;
    const { iconSize } = this.props;
    const iconModifierClass = ImageUpload.classnameBySize.get(iconSize);

    return (
      <div
        className={`image-upload__icon image-upload__icon--${iconModifierClass}`}
        onClick={showCropper}
      >
        <i className={`fa fa-crop fa-${iconSize}x`} />
        {(iconSize as number) > 1 && <p>Crop</p>}
      </div>
    );
  }

  renderRemoveIcon() {
    const { removeImage } = this.props;
    const { iconSize } = this.props;
    const iconModifierClass = ImageUpload.classnameBySize.get(iconSize);

    return (
      <div
        className={`image-upload__icon image-upload__icon--${iconModifierClass}`}
        onClick={removeImage}
      >
        <i className={`fa fa-times fa-${iconSize}x`} />
        {(iconSize as number) > 1 && <p>Remove</p>}
      </div>
    );
  }

  renderDefaultIconArea() {
    const { title, couldBeRemoved, iconSize, hideCropIcon, imgUrl } =
      this.props;
    const iconModifierClass = ImageUpload.classnameBySize.get(iconSize);
    const showBackground = Boolean(imgUrl);

    return (
      <>
        {showBackground && this.renderBackgroundImage()}

        <div
          className={classnames({
            'image-upload__icon-container': true,
            'image-upload__icon-container--visible': !showBackground,
          })}
        >
          <div
            className={`image-upload__icon image-upload__icon--${iconModifierClass}`}
            onClick={this.onUpload}
          >
            <i className={`far fa-image fa-${iconSize}x`} />
            {(iconSize as number) > 1 && <p>{title}</p>}
          </div>
          {!hideCropIcon && showBackground && this.renderCropIcon()}
          {couldBeRemoved && showBackground && this.renderRemoveIcon()}
        </div>
      </>
    );
  }

  render() {
    const { props, state } = this;
    const { className, iconArea, imgUrl } = props;
    const {
      shouldShowCropper,
      shouldShowImageSizeModal,
      uploading,
      shouldCropOnToggle,
      fixedRatio,
    } = state;

    return (
      <div
        className={classnames('image-upload', className, {
          'image-upload--uploading': this.state.uploading,
        })}
      >
        {!uploading && (shouldCropOnToggle || shouldShowCropper) && (
          <CropArea
            onCropCallback={this.crop}
            closeCropper={this.onCancelCropper}
            imgUrl={imgUrl}
            fixedRatio={fixedRatio}
          />
        )}

        <Modal
          isOpen={shouldShowImageSizeModal}
          toggle={this.onImageSizeModalHide}
        >
          <ModalBody>
            <h5>{ImageUploadConstants.OVERSIZE_MESSAGE}</h5>
          </ModalBody>
        </Modal>
        {this.state.uploading ? (
          <div className="sk-circle image-upload__spinner">
            <div className="sk-circle1 sk-child" />
            <div className="sk-circle2 sk-child" />
            <div className="sk-circle3 sk-child" />
            <div className="sk-circle4 sk-child" />
            <div className="sk-circle5 sk-child" />
            <div className="sk-circle6 sk-child" />
            <div className="sk-circle7 sk-child" />
            <div className="sk-circle8 sk-child" />
            <div className="sk-circle9 sk-child" />
            <div className="sk-circle10 sk-child" />
            <div className="sk-circle11 sk-child" />
            <div className="sk-circle12 sk-child" />
          </div>
        ) : iconArea ? (
          iconArea(this.onUpload)
        ) : (
          this.renderDefaultIconArea()
        )}

        <input
          type="file"
          accept="image/*"
          ref={this.ref}
          style={{
            display: 'none',
          }}
          onChange={this.onFileUpload}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openImageUploadFormModal: (onNextButtonClick, onCancelButtonClick) =>
    dispatch(openImageUploadFormModal(onNextButtonClick, onCancelButtonClick)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

// @ts-ignore Issue with default props and recompose mapDispatchToProps
export default enhance(ImageUpload);
